import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef, ReactNode } from 'react';
import { PaletteMode } from '@mui/material';

interface ThemeModeContextValue {
  mode: PaletteMode;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

interface ThemeModeProviderProps {
  children: ReactNode;
}

const lightQuery = '(prefers-color-scheme: light)';

const readStoredMode = (): PaletteMode | null => {
  try {
    const stored = localStorage.getItem('themeMode');
    return (stored === 'light' || stored === 'dark') ? stored : null;
  } catch {
    return null;
  }
};

const systemMode = (): PaletteMode =>
  typeof window !== 'undefined' && window.matchMedia?.(lightQuery).matches ? 'light' : 'dark';

// Follows the visitor's system setting until they pick a mode with the header toggle;
// that choice is saved in localStorage("themeMode") and also read by the static pages.
export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(() => readStoredMode() ?? systemMode());
  // Set once the visitor toggles, so their choice holds for this visit even if storage is blocked.
  const pickedThisVisit = useRef(false);

  useEffect(() => {
    const media = window.matchMedia?.(lightQuery);
    if (!media) return;
    const onChange = (event: MediaQueryListEvent) => {
      if (!pickedThisVisit.current && !readStoredMode()) setMode(event.matches ? 'light' : 'dark');
    };
    // Safari before 14 only has the older addListener API.
    if (media.addEventListener) {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  const toggleMode = useCallback(() => {
    pickedThisVisit.current = true;
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem('themeMode', next);
      } catch {
        // Storage can be blocked (private mode); the toggle still works for this visit.
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeContext;
